/* ============================================================
   outofspaces® — Spiral Intro Animation
   Translated from React/TypeScript → Vanilla JS
   Requires: GSAP (loaded via CDN in theme.liquid)
   ============================================================ */

class Vector2D {
  constructor(x, y) { this.x = x; this.y = y; }
  static random(min, max) { return min + Math.random() * (max - min); }
}

class Vector3D {
  constructor(x, y, z) { this.x = x; this.y = y; this.z = z; }
}

/* ── Star particle ──────────────────────────────────────────── */
class Star {
  constructor(cameraZ, cameraTravelDistance) {
    this.angle           = Math.random() * Math.PI * 2;
    this.distance        = 30 * Math.random() + 15;
    this.rotationDir     = Math.random() > 0.5 ? 1 : -1;
    this.expansionRate   = 1.2 + Math.random() * 0.8;
    this.finalScale      = 0.7 + Math.random() * 0.6;
    this.dx              = this.distance * Math.cos(this.angle);
    this.dy              = this.distance * Math.sin(this.angle);
    this.spiralLocation  = (1 - Math.pow(1 - Math.random(), 3.0)) / 1.3;
    this.z               = Vector2D.random(0.5 * cameraZ, cameraTravelDistance + cameraZ);
    const lerp           = (s, e, t) => s * (1 - t) + e * t;
    this.z               = lerp(this.z, cameraTravelDistance / 2, 0.3 * this.spiralLocation);
    this.strokeWeight    = Math.pow(Math.random(), 2.0);
  }

  render(p, c) {
    const spiralPos = c.spiralPath(this.spiralLocation);
    const q = p - this.spiralLocation;
    if (q <= 0) return;

    const dp = c.constrain(4 * q, 0, 1);
    const lin = dp;
    const ela = c.easeOutElastic(dp);
    const pow = Math.pow(dp, 2);

    let easing;
    if (dp < 0.3)      easing = c.lerp(lin, pow, dp / 0.3);
    else if (dp < 0.7) easing = c.lerp(pow, ela, (dp - 0.3) / 0.4);
    else               easing = ela;

    let sx, sy;
    if (dp < 0.3) {
      sx = c.lerp(spiralPos.x, spiralPos.x + this.dx * 0.3, easing / 0.3);
      sy = c.lerp(spiralPos.y, spiralPos.y + this.dy * 0.3, easing / 0.3);
    } else if (dp < 0.7) {
      const mp = (dp - 0.3) / 0.4;
      const cs = Math.sin(mp * Math.PI) * this.rotationDir * 1.5;
      const bx = spiralPos.x + this.dx * 0.3, by = spiralPos.y + this.dy * 0.3;
      const tx = spiralPos.x + this.dx * 0.7, ty = spiralPos.y + this.dy * 0.7;
      sx = c.lerp(bx, tx, mp) + (-this.dy * 0.4 * cs) * mp;
      sy = c.lerp(by, ty, mp) + ( this.dx * 0.4 * cs) * mp;
    } else {
      const fp = (dp - 0.7) / 0.3;
      const bx = spiralPos.x + this.dx * 0.7, by = spiralPos.y + this.dy * 0.7;
      const td = this.distance * this.expansionRate * 1.5;
      const sa = this.angle + 1.2 * this.rotationDir * fp * Math.PI;
      sx = c.lerp(bx, spiralPos.x + td * Math.cos(sa), fp);
      sy = c.lerp(by, spiralPos.y + td * Math.sin(sa), fp);
    }

    const vx = (this.z - c.cameraZ) * sx / c.viewZoom;
    const vy = (this.z - c.cameraZ) * sy / c.viewZoom;
    let sm = dp < 0.6
      ? 1.0 + dp * 0.2
      : c.lerp(1.2, this.finalScale, (dp - 0.6) / 0.4);

    c.showProjectedDot(new Vector3D(vx, vy, this.z), 8.5 * this.strokeWeight * sm);
  }
}

/* ── Animation controller ───────────────────────────────────── */
class SpiralController {
  constructor(canvas, ctx, dpr, width, height) {
    this.canvas               = canvas;
    this.ctx                  = ctx;
    this.dpr                  = dpr;
    this.width                = width;
    this.height               = height;
    this.time                 = 0;
    this.stars                = [];
    this.changeEventTime      = 0.32;
    this.cameraZ              = -400;
    this.cameraTravelDistance = 3400;
    this.startDotYOffset      = 28;
    this.viewZoom             = 100;
    this.trailLength          = 80;

    /* Seed-based random for deterministic stars */
    const orig = Math.random;
    let seed = 1234;
    Math.random = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    for (let i = 0; i < 5000; i++) this.stars.push(new Star(this.cameraZ, this.cameraTravelDistance));
    Math.random = orig;

    this._setupTimeline();
  }

  _setupTimeline() {
    const self = this;
    const duration = 15000; /* 15 seconds, matching original */
    let startTime = null;

    function tick(timestamp) {
      if (!startTime) startTime = timestamp;
      self.time = ((timestamp - startTime) / duration) % 1;
      self.render();
      self._rafId = requestAnimationFrame(tick);
    }
    this._rafId = requestAnimationFrame(tick);
  }

  /* ── Math helpers ── */
  ease(p, g)               { return p < 0.5 ? 0.5 * Math.pow(2*p,g) : 1 - 0.5 * Math.pow(2*(1-p),g); }
  easeOutElastic(x)        { if (x<=0) return 0; if (x>=1) return 1; const c4=(2*Math.PI)/4.5; return Math.pow(2,-8*x)*Math.sin((x*8-0.75)*c4)+1; }
  map(v,s1,e1,s2,e2)      { return s2+(e2-s2)*((v-s1)/(e1-s1)); }
  constrain(v,mn,mx)       { return Math.min(Math.max(v,mn),mx); }
  lerp(s,e,t)              { return s*(1-t)+e*t; }

  spiralPath(p) {
    p = this.constrain(1.2*p, 0, 1);
    p = this.ease(p, 1.8);
    const theta = 2*Math.PI*6*Math.sqrt(p);
    const r = 170*Math.sqrt(p);
    return new Vector2D(r*Math.cos(theta), r*Math.sin(theta)+this.startDotYOffset);
  }

  rotate(v1, v2, p, orientation) {
    const mid = new Vector2D((v1.x+v2.x)/2,(v1.y+v2.y)/2);
    const dx=v1.x-mid.x, dy=v1.y-mid.y;
    const angle=Math.atan2(dy,dx), o=orientation?-1:1;
    const r=Math.sqrt(dx*dx+dy*dy);
    const bounce=Math.sin(p*Math.PI)*0.05*(1-p);
    return new Vector2D(
      mid.x+r*(1+bounce)*Math.cos(angle+o*Math.PI*this.easeOutElastic(p)),
      mid.y+r*(1+bounce)*Math.sin(angle+o*Math.PI*this.easeOutElastic(p))
    );
  }

  showProjectedDot(pos, sizeFactor) {
    const t2 = this.constrain(this.map(this.time,this.changeEventTime,1,0,1),0,1);
    const camZ = this.cameraZ + this.ease(Math.pow(t2,1.2),1.8)*this.cameraTravelDistance;
    if (pos.z <= camZ) return;
    const d = pos.z - camZ;
    const x = this.viewZoom*pos.x/d, y = this.viewZoom*pos.y/d;
    const sw = 400*sizeFactor/d;
    this.ctx.lineWidth = sw;
    this.ctx.beginPath();
    this.ctx.arc(x, y, 0.5, 0, Math.PI*2);
    this.ctx.fill();
  }

  render() {
    const ctx = this.ctx;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.save();
    ctx.translate(this.width/2, this.height/2);

    const t1 = this.constrain(this.map(this.time,0,this.changeEventTime+0.25,0,1),0,1);
    const t2 = this.constrain(this.map(this.time,this.changeEventTime,1,0,1),0,1);
    ctx.rotate(-Math.PI*this.ease(t2,2.7));

    /* Trail */
    for (let i = 0; i < this.trailLength; i++) {
      const f  = this.map(i,0,this.trailLength,1.1,0.1);
      const sw = (1.3*(1-t1)+3.0*Math.sin(Math.PI*t1))*f;
      ctx.fillStyle = 'white';
      ctx.lineWidth = sw;
      const pos    = this.spiralPath(t1 - 0.00015*i);
      const off    = new Vector2D(pos.x+5, pos.y+5);
      const rotated = this.rotate(pos, off, Math.sin(this.time*Math.PI*2)*0.5+0.5, i%2===0);
      ctx.beginPath();
      ctx.arc(rotated.x, rotated.y, sw/2, 0, Math.PI*2);
      ctx.fill();
    }

    /* Stars */
    ctx.fillStyle = 'white';
    this.stars.forEach(s => s.render(t1, this));

    /* Start dot */
    if (this.time > this.changeEventTime) {
      const dy = this.cameraZ*this.startDotYOffset/this.viewZoom;
      this.showProjectedDot(new Vector3D(0, dy, this.cameraTravelDistance), 2.5);
    }

    ctx.restore();
  }

  destroy() { cancelAnimationFrame(this._rafId); }
}

/* ── Bootstrap ──────────────────────────────────────────────── */
function _oosInitSpiral() {
  const wrapper = document.getElementById('oos-spiral-intro');
  if (!wrapper) return;

  /* Skip if already seen this session — hide immediately before paint */
  if (sessionStorage.getItem('oos-spiral-seen')) {
    wrapper.style.display = 'none';
    return;
  }

  /* Lock scroll while spiral is showing */
  document.body.style.overflow = 'hidden';

  const canvas   = wrapper.querySelector('#oos-spiral-canvas');
  const enterBtn = wrapper.querySelector('#oos-spiral-enter');
  const logoBtn  = wrapper.querySelector('#oos-spiral-logo-btn');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  let controller = null;

  function resize() {
    const w = window.innerWidth, h = window.innerHeight;
    canvas.width  = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width  = w + 'px';
    canvas.style.height = h + 'px';
    /* setTransform resets any prior transform, so repeated resizes stay exact */
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (controller) { controller.width = w; controller.height = h; }
  }
  resize();

  controller = new SpiralController(canvas, ctx, dpr, window.innerWidth, window.innerHeight);

  /* Fade in logo immediately, ENTER button after 2s */
  if (logoBtn) logoBtn.style.opacity = '1';
  /* Arabic name: letters fade in one by one after the logo lands.
     Spans only change colour (not font), so Arabic letters keep their joins. */
  var arEl = wrapper.querySelector('#oos-spiral-ar');
  var enterDelay = 2000;
  if (arEl) {
    var chars = Array.from(arEl.dataset.text || '');
    var STAGGER = 150, START = 1900;
    arEl.innerHTML = chars.map(function (ch) {
      return ch === ' ' ? ' ' : '<span class="ar-l">' + ch + '</span>';
    }).join('');
    setTimeout(function () { arEl.classList.add('is-lined'); }, 1300);
    var letters = arEl.querySelectorAll('.ar-l');
    letters.forEach(function (el, i) {
      setTimeout(function () { el.classList.add('is-on'); }, START + i * STAGGER);
    });
    enterDelay = START + letters.length * STAGGER + 900;
  }
  setTimeout(function () {
    if (enterBtn) enterBtn.classList.add('is-visible');
  }, enterDelay);

  /* ENTER → fade out → reveal page underneath */
  function dismiss() {
    sessionStorage.setItem('oos-spiral-seen', '1');
    wrapper.classList.add('is-exiting');
    setTimeout(function () {
      controller.destroy();
      document.body.style.overflow = '';
      wrapper.remove();
    }, 850);
  }

  if (enterBtn) enterBtn.addEventListener('click', dismiss);

  /* Resize handler */
  window.addEventListener('resize', resize, { passive: true });
}
/* Run immediately if DOM is ready, otherwise wait */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _oosInitSpiral);
} else {
  _oosInitSpiral();
}
